import Vue from 'vue'
import store from 'store'
import { mapState } from 'vuex'
import $ from 'jquery'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import brandTagDisplay from 'mixins/brand-tag'

export default el => Promise.all([
  enqueueModule('product-form.vue'),
  enqueueModule('product-hero.vue')
]).then(() => new Vue({
  el,
  store,
  mixins: [brandTagDisplay],
  computed: {
    ...mapState('product', [
      'product'
    ])
  },
  mounted () {
    const getRoot = () => this.$el || el
    const sizePopups = $('.js-product-size-popup')
    const sizePopupOverlays = $('.js-product-size-popup-overlay')
    const sizePopupPageClass = 'product-size-popup-open'
    let activeSizePopup = $()
    let activeSizePopupOverlay = $()
    let activeSizeScrollContainer = null
    const focusableSelector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')
    const dialogFocusState = {
      sizePopup: null,
      redesignDrawer: null
    }

    function isVisible (node) {
      return !!(node && (node.offsetWidth || node.offsetHeight || node.getClientRects().length))
    }

    function getFocusableElements (container) {
      if (!container) return []

      return Array.from(container.querySelectorAll(focusableSelector)).filter(isVisible)
    }

    function focusDialogElement (container, selector) {
      if (!container) return

      const preferredElement = selector ? container.querySelector(selector) : null
      const fallbackElement = getFocusableElements(container)[0]
      const target = isVisible(preferredElement) ? preferredElement : (fallbackElement || container)

      if (target && typeof target.focus === 'function') {
        target.focus()
      }
    }

    function saveDialogFocus (key, trigger) {
      const target = trigger instanceof HTMLElement ? trigger : document.activeElement
      dialogFocusState[key] = target instanceof HTMLElement ? target : null
    }

    function restoreDialogFocus (key) {
      const target = dialogFocusState[key]
      dialogFocusState[key] = null

      if (target && document.contains(target) && typeof target.focus === 'function') {
        target.focus()
      }
    }

    function trapDialogFocus (event, container) {
      if (!container || event.key !== 'Tab') return

      const focusableElements = getFocusableElements(container)

      if (!focusableElements.length) {
        event.preventDefault()
        container.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement

      if (!container.contains(activeElement)) {
        event.preventDefault()
        ;(event.shiftKey ? lastElement : firstElement).focus()
        return
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    function getRedesignDrawerElements () {
      const root = getRoot()

      return {
        root,
        drawer: root ? $(root).find('.js-redesign-drawer').first() : $(),
        backdrop: root ? $(root).find('.js-redesign-drawer-backdrop').first() : $()
      }
    }

    function closeSizePopup (restoreFocus = true) {
      if (activeSizeScrollContainer) {
        enableBodyScroll(activeSizeScrollContainer)
      }

      activeSizePopup.removeClass('active').attr('aria-hidden', 'true')
      activeSizePopupOverlay.removeClass('active').attr('aria-hidden', 'true')
      $('.js-size-guide-trigger').attr('aria-expanded', 'false')
      document.documentElement.classList.remove(sizePopupPageClass)
      document.body.classList.remove(sizePopupPageClass)

      activeSizePopup = $()
      activeSizePopupOverlay = $()
      activeSizeScrollContainer = null

      if (restoreFocus) {
        restoreDialogFocus('sizePopup')
      }
    }

    function openSizePopup (trigger) {
      const target = trigger.dataset.productGuideTarget || 'size-guide'
      const targetPopup = sizePopups.filter(function () {
        return this.dataset.productGuidePopup === target
      }).first()
      const targetOverlay = sizePopupOverlays.filter(function () {
        return this.dataset.productGuideOverlay === target
      }).first()

      if (!targetPopup.length) return

      closeSizePopup(false)
      saveDialogFocus('sizePopup', trigger)
      activeSizePopup = targetPopup
      activeSizePopupOverlay = targetOverlay
      activeSizeScrollContainer = activeSizePopup.find('.size-popup-content').get(0) || activeSizePopup.get(0)
      activeSizePopup.addClass('active').attr('aria-hidden', 'false')
      activeSizePopupOverlay.addClass('active').attr('aria-hidden', 'false')
      trigger.setAttribute('aria-expanded', 'true')
      document.documentElement.classList.add(sizePopupPageClass)
      document.body.classList.add(sizePopupPageClass)
      disableBodyScroll(activeSizeScrollContainer, { reserveScrollBarGap: true })
      focusDialogElement(activeSizePopup.get(0), '.js-size-guide-close')
    }

    function setRedesignDrawerTab ($drawer, target) {
      if (!$drawer.length) return

      $drawer.find('.js-redesign-drawer-tab').each(function () {
        const isActive = this.dataset.redesignDrawerTab === target
        this.classList.toggle('active', isActive)
        this.setAttribute('aria-selected', isActive ? 'true' : 'false')
      })

      $drawer.find('[data-redesign-drawer-panel]').each(function () {
        this.classList.toggle('active', this.dataset.redesignDrawerPanel === target)
      })
    }

    function openRedesignDrawer (target, trigger) {
      const { drawer, backdrop } = getRedesignDrawerElements()
      if (!drawer.length) return

      saveDialogFocus('redesignDrawer', trigger)
      setRedesignDrawerTab(drawer, target)
      drawer.addClass('is-open').attr('aria-hidden', 'false')
      backdrop.addClass('is-open')
      document.documentElement.classList.add('redesign-drawer-open')
      document.body.classList.add('redesign-drawer-open')
      disableBodyScroll(drawer.get(0), { reserveScrollBarGap: true })
      focusDialogElement(drawer.get(0), `.js-redesign-drawer-tab[data-redesign-drawer-tab="${target}"]`)
    }

    function closeRedesignDrawer (restoreFocus = true) {
      const { drawer, backdrop } = getRedesignDrawerElements()
      if (!drawer.length) return

      drawer.removeClass('is-open').attr('aria-hidden', 'true')
      backdrop.removeClass('is-open')
      document.documentElement.classList.remove('redesign-drawer-open')
      document.body.classList.remove('redesign-drawer-open')
      enableBodyScroll(drawer.get(0))

      if (restoreFocus) {
        restoreDialogFocus('redesignDrawer')
      }
    }

    $(document).on('click', '.js-tab-wrapper .js-tab-item .js-tab-item-title', function (e) {
      e.preventDefault()
      const $item = $(this).closest('.js-tab-item')
      const $cover = $(this).closest('.js-tab-wrapper')
      $cover.find('.js-tab-item').not($item).removeClass('active')
      $cover.find('.js-tab-item .js-tab-item-title').not(this).attr('aria-expanded', 'false')
      $item.toggleClass('active')
      this.setAttribute('aria-expanded', $item.hasClass('active') ? 'true' : 'false')
    })

    $(document).on('click', '.js-size-guide-trigger', function (e) {
      e.preventDefault()

      const target = this.dataset.productGuideTarget || 'size-guide'
      const isCurrentPopupActive = activeSizePopup.data('product-guide-popup') === target

      if (isCurrentPopupActive) {
        closeSizePopup()
        return
      }

      openSizePopup(this)
    })

    $(document).click(function (e) {
      if (activeSizePopup.length && $(e.target).closest('.js-product-size-popup, .js-size-guide-trigger').length === 0) {
        closeSizePopup()
      }
    })

    sizePopupOverlays.on('click', function () {
      if (activeSizePopupOverlay.is(this)) {
        closeSizePopup()
      }
    })

    $(document).on('click', '.js-size-guide-close', function (e) {
      e.preventDefault()
      closeSizePopup()
    })

    $(document).on('keydown', function (e) {
      if (e.key === 'Escape' && activeSizePopup.length) {
        e.preventDefault()
        closeSizePopup()
        return
      }

      if (activeSizePopup.length) {
        trapDialogFocus(e, activeSizePopup.get(0))
      }
    })

    $(document).on('click', '.js-redesign-drawer-open', function (e) {
      const root = getRoot()
      if (!root || !root.contains(this)) return

      e.preventDefault()

      const target = this.dataset.redesignDrawerTarget
      openRedesignDrawer(target, this)
    })

    $(document).on('click', '.js-redesign-drawer-close, .js-redesign-drawer-backdrop', function (e) {
      const root = getRoot()
      if (!root || !root.contains(this)) return

      e.preventDefault()
      closeRedesignDrawer()
    })

    $(document).on('click', '.js-redesign-drawer-tab', function (e) {
      const root = getRoot()
      if (!root || !root.contains(this)) return

      e.preventDefault()

      const target = this.dataset.redesignDrawerTab
      const $drawer = getRedesignDrawerElements().drawer
      if (!$drawer.length) return

      setRedesignDrawerTab($drawer, target)
    })

    $(document).on('keydown', function (e) {
      const { drawer } = getRedesignDrawerElements()
      const $drawer = drawer

      if (e.key === 'Escape' && $drawer.hasClass('is-open')) {
        e.preventDefault()
        closeRedesignDrawer()
        return
      }

      if ($drawer.hasClass('is-open')) {
        trapDialogFocus(e, $drawer.get(0))
      }
    })
  }
}))
